import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LabNavigation from '@/components/LabNavigation';
import {
  NotebookBackground,
  PageFlip,
  DoodleStar,
  DoodleArrow,
  DoodleLightbulb,
  DoodleCheckmark,
  DoodleSpiral,
  DoodleHeart,
  CoffeeStain,
  WashiTape,
  StickyNote,
  PaperClip,
  SketchyBorder,
} from '@/components/notebook';
import { useNotebookSounds } from '@/hooks/useNotebookSounds';
import { notebookPalette } from '@/lib/notebookTheme';

interface LabStory {
  id: string;
  number: string;
  title: string;
  subtitle: string;
  question: string;
  description: string;
  tags: string[];
  path: string;
  marginNote?: string;
  doodle?: 'star' | 'lightbulb' | 'heart' | 'checkmark' | 'spiral';
  accentColor?: string;
}

const labStories: LabStory[] = [
  {
    id: '100-lines',
    number: '01',
    title: '100 Lines of Code',
    subtitle: 'The Constraint Manifesto',
    question: 'What if less code meant better code?',
    description: '164+ projects. Each under 100 lines. Proof that elegance trumps complexity. A community of 200+ contributors proving that limitations breed creativity.',
    tags: ['717★', '332 forks', 'Open Source'],
    path: '/lab/100-lines',
    marginNote: 'Less is more!',
    doodle: 'star',
    accentColor: notebookPalette.coloredPencil.yellow,
  },
  {
    id: 'founding-engineer',
    number: '02',
    title: 'First Commit to Million Lives',
    subtitle: 'The Founding Engineer Journey',
    question: 'What does building from 0 to 1 actually look like?',
    description: 'Five years at PlumHQ. From a 10-person startup to serving 500,000+ employees across 4,000+ companies. The story of scaling healthcare tech.',
    tags: ['5 years', '$20.7M raised', 'Healthcare'],
    path: '/lab/founding-engineer',
    marginNote: 'The early days...',
    doodle: 'heart',
    accentColor: notebookPalette.coloredPencil.orange,
  },
  {
    id: 'mcp-jest',
    number: '03',
    title: 'MCP-Jest',
    subtitle: 'The First Testing Framework',
    question: 'Who tests the tools that test AI?',
    description: 'When MCP exploded, testing was an afterthought. I built the first Jest-like framework for MCP servers. Because even AI needs reliable tests.',
    tags: ['First ever', '500+ downloads', 'MIT'],
    path: '/lab/mcp-jest',
    marginNote: 'Testing matters!',
    doodle: 'checkmark',
    accentColor: notebookPalette.coloredPencil.blue,
  },
  {
    id: 'ai-terminal-course',
    number: '04',
    title: 'AI Terminal Course',
    subtitle: 'vimtutor for AI Engineering',
    question: 'Why do tutorials never stick?',
    description: 'I watched 100 hours of AI tutorials. Built nothing. So I created the first terminal-only AI course. Type every command. See real API costs.',
    tags: ['20 modules', '$10 total', 'npm'],
    path: '/lab/ai-terminal-course',
    marginNote: 'Learn by doing',
    doodle: 'lightbulb',
    accentColor: notebookPalette.coloredPencil.green,
  },
  {
    id: 'unengineered',
    number: '05',
    title: 'Unengineered',
    subtitle: 'The Case for Simplicity',
    question: 'Why do engineers make simple things complicated?',
    description: 'A blog about stripping away jargon, questioning assumptions, and explaining complex ideas in plain language.',
    tags: ['Writing', 'Substack', 'Philosophy'],
    path: '/lab/unengineered',
    marginNote: 'Keep it simple',
    doodle: 'spiral',
    accentColor: notebookPalette.coloredPencil.pink,
  },
  {
    id: 'open-source-playbook',
    number: '06',
    title: 'The Open Source Playbook',
    subtitle: '7 Years of Giving Code Away',
    question: 'Is open source actually worth it?',
    description: 'From FOSSASIA 2018 to GSoC to 1,600+ stars. A 7-year experiment in giving your work away and why it might be the best career move.',
    tags: ['1,600+ ★', 'GSoC', '7 years'],
    path: '/lab/open-source-playbook',
    marginNote: 'Give to receive',
    doodle: 'star',
    accentColor: notebookPalette.coloredPencil.blue,
  },
  {
    id: 'abstraction-stack',
    number: '07',
    title: 'The Abstraction Stack',
    subtitle: '12 Layers to Electrons',
    question: 'How far is your code from electrons?',
    description: 'Trace console.log through V8, syscalls, kernel, CPU pipeline to physical transistors. The true cost of "simple" code.',
    tags: ['12 layers', '6ms', 'Interactive'],
    path: '/lab/abstraction-stack',
    marginNote: 'Deep dive!',
    doodle: 'spiral',
    accentColor: notebookPalette.coloredPencil.purple,
  },
  {
    id: 'meeting-tax',
    number: '08',
    title: 'The Meeting Tax',
    subtitle: 'The Calculator Nobody Wanted',
    question: 'What did that standup actually cost?',
    description: 'Interactive calculator revealing the true cost of meetings. Direct salary, recovery time, productivity loss, deep work destruction.',
    tags: ['Calculator', '$37B/year', 'Research'],
    path: '/lab/meeting-tax',
    marginNote: 'Time = money',
    doodle: 'lightbulb',
    accentColor: notebookPalette.coloredPencil.red,
  },
  {
    id: 'interview-paradox',
    number: '09',
    title: 'The Interview Paradox',
    subtitle: 'The Great Disconnect',
    question: 'Why do we test what we never use?',
    description: 'Data visualization of interview questions vs actual job tasks. The absurdity of leetcode culture, visualized.',
    tags: ['Data Viz', 'Satire', 'Interactive'],
    path: '/lab/interview-paradox',
    marginNote: 'The irony...',
    doodle: 'spiral',
    accentColor: notebookPalette.coloredPencil.blue,
  },
  {
    id: 'keyboard-journey',
    number: '10',
    title: "The Keyboard's Journey",
    subtitle: '9 Layers Deep',
    question: 'What happens when you press Enter?',
    description: 'Press any key, watch it travel through USB, interrupts, drivers, OS, browser, to pixel. The butterfly effect of input.',
    tags: ['Interactive', '9 layers', 'Real-time'],
    path: '/lab/keyboard-journey',
    marginNote: 'Every keypress...',
    doodle: 'lightbulb',
    accentColor: notebookPalette.coloredPencil.green,
  },
  {
    id: 'learning-ai',
    number: '11',
    title: 'Learning AI by Building AI',
    subtitle: 'The TGSD Story',
    question: 'What does building an AI product actually teach you?',
    description: 'Three builders tried to create an OS for work. With Prateek Jogani (CTO, Qoala) and Abhishek Bagaria. Six lessons from the trenches of applied AI.',
    tags: ['AI', 'Future of Work', '2023'],
    path: '/lab/learning-ai',
    marginNote: 'Ship to learn',
    doodle: 'lightbulb',
    accentColor: notebookPalette.coloredPencil.purple,
  },
];

const DoodleComponent: React.FC<{ type?: string; color?: string }> = ({ type, color }) => {
  switch (type) {
    case 'star': return <DoodleStar size={28} color={color} animated />;
    case 'lightbulb': return <DoodleLightbulb size={28} color={color} animated />;
    case 'heart': return <DoodleHeart size={28} color={color} animated />;
    case 'checkmark': return <DoodleCheckmark size={28} color={color} animated />;
    case 'spiral': return <DoodleSpiral size={28} color={color} animated />;
    default: return <DoodleStar size={28} color={color} animated />;
  }
};

const Labs = () => {
  const navigate = useNavigate();
  const [showIntro, setShowIntro] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const { playPageFlip, playPencilScratch, playClick } = useNotebookSounds();

  const handleNext = useCallback(() => {
    setDirection(1);
    setActiveIndex((prev) => (prev + 1) % labStories.length);
    playPageFlip();
  }, [playPageFlip]);

  const handlePrev = useCallback(() => {
    setDirection(-1);
    setActiveIndex((prev) => (prev - 1 + labStories.length) % labStories.length);
    playPageFlip();
  }, [playPageFlip]);

  const handleNavigate = useCallback((index: number) => {
    setDirection(index > activeIndex ? 1 : -1);
    setActiveIndex(index);
    playPageFlip();
  }, [activeIndex, playPageFlip]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (showIntro) {
      if (e.key === 'Enter') {
        playClick();
        setShowIntro(false);
      }
      return;
    }

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      handleNext();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      handlePrev();
    } else if (e.key === 'Enter') {
      playClick();
      navigate(labStories[activeIndex].path);
    }
  }, [activeIndex, showIntro, handleNext, handlePrev, navigate, playClick]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const activeStory = labStories[activeIndex];

  // Intro/Landing Screen - Lab Notebook Cover (Sketchy Style)
  if (showIntro) {
    return (
      <div className="min-h-screen bg-[#fdfcf7] flex flex-col relative overflow-hidden">
        {/* Graph paper pattern */}
        <div
          className="absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage: `
              linear-gradient(#e8e0d5 1px, transparent 1px),
              linear-gradient(90deg, #e8e0d5 1px, transparent 1px)
            `,
            backgroundSize: '24px 24px',
          }}
        />

        {/* Paper texture noise */}
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Sketchy border frame */}
        <svg className="absolute inset-4 md:inset-8 w-[calc(100%-2rem)] md:w-[calc(100%-4rem)] h-[calc(100%-2rem)] md:h-[calc(100%-4rem)] pointer-events-none">
          <rect
            x="2"
            y="2"
            width="calc(100% - 4px)"
            height="calc(100% - 4px)"
            fill="none"
            stroke="#9ca3af"
            strokeWidth="2"
            strokeDasharray="8 4"
            rx="8"
          />
        </svg>

        {/* Navigation */}
        <div className="relative z-10 flex items-center justify-between px-8 py-6">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium font-typewriter">Back</span>
          </motion.button>
        </div>

        {/* Main Content - Notebook Cover */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-8 md:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center space-y-8"
          >
            {/* Label - like a handwritten sticker */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-block"
            >
              <div className="px-6 py-3 bg-[#fef9c3] border-2 border-gray-300 shadow-md transform -rotate-1 rounded-sm"
                style={{ boxShadow: '3px 3px 0 rgba(0,0,0,0.1)' }}
              >
                <span className="font-handwritten text-xl text-gray-700">
                  Property of Harsh Joshi
                </span>
              </div>
            </motion.div>

            {/* Title - Sketchy hand-drawn style */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-6xl md:text-8xl font-sketch font-black tracking-tight text-gray-800"
              style={{
                textShadow: '2px 2px 0 #d1d5db, 4px 4px 0 #e5e7eb',
              }}
            >
              LAB NOTEBOOK
            </motion.h1>

            {/* Subtitle in handwritten style */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="font-handwritten text-2xl md:text-3xl text-gray-600"
            >
              experiments, discoveries & deep dives
            </motion.p>

            {/* Story count badge as sticky note */}
            <motion.div
              initial={{ opacity: 0, rotate: -10 }}
              animate={{ opacity: 1, rotate: 3 }}
              transition={{ delay: 0.5, type: 'spring' }}
              className="inline-block"
            >
              <StickyNote color="yellow" size="small" rotation={3}>
                <span className="font-handwritten text-lg">{labStories.length} stories</span>
              </StickyNote>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="pt-8"
            >
              <button
                onClick={() => {
                  playClick();
                  setShowIntro(false);
                }}
                className="group inline-flex items-center gap-4 px-8 py-4 bg-gray-800 text-white font-medium rounded-sm hover:bg-gray-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-1 border-2 border-gray-700"
                style={{ boxShadow: '4px 4px 0 #9ca3af' }}
              >
                <span className="font-sketch text-lg">Open Notebook</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scattered doodles on cover */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ delay: 1 }}
          className="absolute top-1/4 left-[10%]"
        >
          <DoodleStar size={40} color="#9ca3af" animated delay={1.2} />
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-1/3 right-[15%]"
        >
          <DoodleSpiral size={36} color="#6b7280" animated delay={1.4} />
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ delay: 1.3 }}
          className="absolute top-1/3 right-[12%]"
        >
          <DoodleLightbulb size={32} color="#f59e0b" animated delay={1.5} />
        </motion.div>

        {/* Coffee stain for character */}
        <CoffeeStain
          size="medium"
          intensity="light"
          position={{ bottom: '20%', left: '8%' }}
        />

        {/* Hint */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gray-400 text-xs font-typewriter z-10"
        >
          press enter to begin
        </motion.p>
      </div>
    );
  }

  // Main Carousel View - Notebook Pages
  return (
    <div className="min-h-screen bg-[#fdfcf7] relative overflow-hidden">
      {/* Graph paper pattern - matching intro */}
      <div
        className="absolute inset-0 opacity-[0.4]"
        style={{
          backgroundImage: `
            linear-gradient(#e8e0d5 1px, transparent 1px),
            linear-gradient(90deg, #e8e0d5 1px, transparent 1px)
          `,
          backgroundSize: '24px 24px',
        }}
      />

      {/* Paper texture noise */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Navigation Header */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-8 py-4 bg-gradient-to-b from-[#fdfcf7] to-transparent">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium font-typewriter">Back</span>
        </motion.button>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="font-typewriter text-gray-500 text-sm"
        >
          Page {String(activeIndex + 1).padStart(2, '0')} of {String(labStories.length).padStart(2, '0')}
        </motion.div>
      </div>

      {/* Main Content - Notebook Page */}
      <div className="min-h-screen flex items-center justify-center px-4 md:px-8 py-20">
        <div className="w-full max-w-5xl relative">
          {/* Page Tabs + Navigation */}
          <div className="absolute -right-2 top-8 flex flex-col gap-1 z-20">
            {labStories.slice(0, 6).map((story, index) => (
              <motion.button
                key={story.id}
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => handleNavigate(index)}
                className={`px-3 py-2 text-xs font-handwritten rounded-r-md border-l-0 transition-all ${
                  index === activeIndex
                    ? 'bg-[#fef08a] text-gray-800 translate-x-2 shadow-md font-bold'
                    : 'bg-[#f5f0e1] text-gray-500 hover:bg-[#f0e8d8] hover:translate-x-1'
                }`}
                style={{
                  borderTop: '1px solid #d1d5db',
                  borderRight: '1px solid #d1d5db',
                  borderBottom: '1px solid #d1d5db',
                }}
              >
                {story.number}
              </motion.button>
            ))}
            {labStories.length > 6 && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="px-3 py-1 text-xs text-gray-400 font-handwritten"
              >
                +{labStories.length - 6}
              </motion.span>
            )}

            {/* Navigation Arrows - right below tabs */}
            <div className="flex flex-col gap-2 mt-3 items-center">
              <button
                onClick={handlePrev}
                className="w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center text-gray-600 hover:text-gray-900 hover:shadow-lg transition-all border border-gray-200"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={handleNext}
                className="w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center text-gray-600 hover:text-gray-900 hover:shadow-lg transition-all border border-gray-200"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Notebook Page */}
          <PageFlip
            pageKey={activeStory.id}
            direction={direction}
            className="w-full"
            onFlipComplete={() => playPencilScratch()}
          >
            <NotebookBackground
              variant="blank"
              marginLine
              holes
              aging="light"
              className="min-h-[70vh] md:min-h-[80vh] rounded-lg shadow-2xl"
            >
              <div className="p-6 md:p-10 lg:p-12 relative">
                {/* Paper clip decoration */}
                {activeIndex % 3 === 0 && (
                  <PaperClip color="silver" position="top-right" rotation={12} />
                )}

                {/* Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Left Margin - Doodles & Notes */}
                  <div className="hidden lg:flex flex-col items-end gap-6 pr-4 border-r-2 border-[#f8b4b4]">
                    {/* Margin Note */}
                    {activeStory.marginNote && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                        className="text-right"
                      >
                        <span className="font-handwritten text-sm text-gray-500 italic">
                          → {activeStory.marginNote}
                        </span>
                      </motion.div>
                    )}

                    {/* Doodle */}
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.5, type: 'spring' }}
                    >
                      <DoodleComponent
                        type={activeStory.doodle}
                        color={activeStory.accentColor}
                      />
                    </motion.div>

                    {/* Page Number */}
                    <div className="mt-auto font-handwritten text-6xl text-gray-200">
                      {activeStory.number}
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* Subtitle with washi tape */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <WashiTape
                        color={
                          activeIndex % 5 === 0 ? 'yellow' :
                          activeIndex % 5 === 1 ? 'pink' :
                          activeIndex % 5 === 2 ? 'blue' :
                          activeIndex % 5 === 3 ? 'green' : 'purple'
                        }
                        rotation={-2}
                      >
                        {activeStory.subtitle}
                      </WashiTape>
                    </motion.div>

                    {/* Title */}
                    <motion.h1
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-4xl md:text-5xl lg:text-6xl font-sketch text-gray-800 leading-tight"
                    >
                      {activeStory.title}
                    </motion.h1>

                    {/* Question */}
                    <motion.p
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.25 }}
                      className="text-xl md:text-2xl font-handwritten text-gray-600 italic"
                    >
                      "{activeStory.question}"
                    </motion.p>

                    {/* Description */}
                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="text-lg text-gray-700 leading-relaxed font-serif"
                    >
                      {activeStory.description}
                    </motion.p>

                    {/* Tags as stickers */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="flex flex-wrap gap-3 pt-4"
                    >
                      {activeStory.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="px-4 py-2 rounded-full text-sm font-handwritten bg-white/80 text-gray-700 border border-gray-200 shadow-sm"
                          style={{
                            transform: `rotate(${(i - 1) * 2}deg)`,
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </motion.div>

                    {/* CTA */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="pt-8"
                    >
                      <button
                        onClick={() => {
                          playClick();
                          navigate(activeStory.path);
                        }}
                        className="group inline-flex items-center gap-3 font-sketch text-lg text-gray-700 hover:text-gray-900 transition-colors"
                      >
                        <span className="border-b-2 border-dashed border-gray-400 group-hover:border-gray-700">
                          Read the full story
                        </span>
                        <DoodleArrow direction="right" size={28} color={activeStory.accentColor} animated />
                      </button>
                    </motion.div>
                  </div>
                </div>

                {/* Coffee stain decoration (occasional) */}
                {activeIndex % 4 === 2 && (
                  <CoffeeStain
                    size="medium"
                    intensity="light"
                    position={{ bottom: '10%', right: '5%' }}
                  />
                )}
              </div>
            </NotebookBackground>
          </PageFlip>
        </div>
      </div>

      {/* Page Indicators */}
      <div className="fixed bottom-6 left-8 z-40">
        <div className="flex items-center gap-2">
          {labStories.map((_, index) => (
            <button
              key={index}
              onClick={() => handleNavigate(index)}
              className={`transition-all duration-300 ${
                index === activeIndex
                  ? 'w-8 h-2 bg-gray-700 rounded-full'
                  : 'w-2 h-2 bg-gray-400 rounded-full hover:bg-gray-500'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Keyboard hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="fixed bottom-6 left-1/2 -translate-x-1/2 text-gray-400 text-xs font-typewriter hidden md:block"
      >
        ← → to flip pages • Enter to read
      </motion.div>

      {/* Lab Navigation Sidebar */}
      <LabNavigation
        variant="light"
        activeIndex={activeIndex}
        onNavigate={handleNavigate}
      />
    </div>
  );
};

export default Labs;
